import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { publicApi, BACKEND, CUSTOMER_TOKEN_KEY } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { MILITARY_VERIFY } from "@/constants/testIds";
import { ShieldCheck, BadgeCheck, Lock, Loader2, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";

// Military verification component (ID.me). ID.me is used ONLY to verify military
// eligibility — the button hands off to the backend, which builds the ID.me
// authorization URL. The frontend never constructs the OAuth URL and never
// receives military documents. Benefit is enforced server-side at checkout.
export default function MilitaryVerifyCard() {
  const { customer, openAuthModal, refresh } = useCustomerAuth();
  const [params, setParams] = useSearchParams();
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState(null); // {verified, community, subgroups, verified_at}
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const cfg = await publicApi.get("/idme/config");
      setEnabled(!!cfg.data?.enabled);
    } catch (e) { setEnabled(false); }
    if (customer) {
      try {
        const r = await publicApi.get("/idme/status");
        setStatus(r.data);
      } catch (e) { setStatus(null); }
    }
    setLoading(false);
  }, [customer]);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // Handle the landing back from ID.me (backend redirects to /account?verify=military&idme=...).
  useEffect(() => {
    if (params.get("verify") !== "military") return;
    const result = params.get("idme");
    if (result === "success") toast.success("Military status verified — your benefit is now active.");
    else if (result === "failure") toast.error("We couldn't verify your military status. Please try again.");
    // clear the query flags then refresh
    const next = new URLSearchParams(params);
    next.delete("verify"); next.delete("idme"); next.delete("result");
    setParams(next, { replace: true });
    loadStatus();
    if (refresh) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verified = !!status?.verified;

  const startVerify = () => {
    if (!customer) { openAuthModal(); return; }
    setStarting(true); // disables repeated clicks
    const token = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    // Full-page hand-off; backend requires auth (accepts ?auth= token).
    window.location.href = `${BACKEND}/api/idme/start?auth=${encodeURIComponent(token || "")}`;
  };

  const removeVerification = async () => {
    try {
      await publicApi.delete("/idme/verification");
      toast.success("Military verification removed.");
      setStatus({ verified: false });
      if (refresh) refresh();
    } catch (e) { toast.error("Could not remove verification."); }
  };

  return (
    <div className="border border-[var(--border-lch)] bg-[var(--panel-2)] rounded-xl p-5" data-testid={MILITARY_VERIFY.card}>
      <div className="flex items-center gap-2 text-[#c9a86a] mb-2">
        <ShieldCheck size={16} />
        <span className="text-xs tracking-widest uppercase">Military Verification</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-[var(--muted)] text-sm"><Loader2 size={14} className="animate-spin" /> Checking status…</div>
      ) : verified ? (
        <div data-testid={MILITARY_VERIFY.verifiedBadge}>
          <div className="flex items-center gap-2 text-[var(--text)]">
            <BadgeCheck size={18} className="text-[#c9a86a] shrink-0" />
            <span className="text-sm font-medium">Military Status Verified</span>
          </div>
          {status?.verified_at && (
            <p className="text-xs text-[var(--muted)] mt-1">Verified on {fmtDate(status.verified_at)}</p>
          )}
          <p className="text-sm text-[#c9a86a] mt-2">Your military appreciation benefit is applied automatically at checkout.</p>
          <button onClick={removeVerification} data-testid="idme-remove-button"
            className="mt-3 inline-flex items-center gap-1.5 text-xs text-[var(--muted)] hover:text-[var(--text-2)] transition-colors">
            <RotateCcw size={12} /> Remove verification
          </button>
        </div>
      ) : (
        <>
          <p className="text-sm text-[var(--text-2)] mb-3">
            Verification is completed securely by ID.me. We do not receive your military documents.
          </p>
          <Button
            type="button"
            onClick={startVerify}
            disabled={!enabled || starting}
            data-testid={MILITARY_VERIFY.verifyButton}
            title={enabled ? "Verify with ID.me" : "Coming soon"}
            className="w-full justify-center gap-2 bg-[#c9a86a] hover:bg-[#b89455] text-[var(--on-gold)] font-semibold rounded-none disabled:bg-[var(--bg)] disabled:text-[var(--muted)] disabled:border disabled:border-[var(--border-2)] disabled:cursor-not-allowed"
          >
            {starting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
            {starting ? "Redirecting to ID.me…" : "Verify Military Status with ID.me"}
            {!enabled && <span className="ml-1 text-[10px] uppercase tracking-wider">Coming soon</span>}
          </Button>
          {!enabled && <p className="text-[11px] text-[var(--muted)] mt-2 text-center">Verification opens soon.</p>}
        </>
      )}
    </div>
  );
}
